var _excluded = ["children", "i18n", "id", "inline", "items", "leftHeaderArea", "leadingHeaderArea", "onClose", "onScrollEnd", "placement", "rightHeaderArea", "scrollEndOffset", "trailingHeaderArea"],
  _excluded2 = ["id"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import Transition from 'react-transition-group/Transition';
import { useTheme } from '@veneer/theme';
import mergeRefs from 'react-merge-refs';
import NotificationCenterItem from '../notification_center_item';
import Popover from '../shared/popover';
import scrollEnded from '../utils/scroll_ended';
import useStyles from './use_styles';
import Scrollbar from '../scrollbar';
import notificationCenterI18n from './i18n';
import { propTypes } from './prop_types';
import Button from '../button';
import NotificationBadge from '../notification_badge';
import { jsx as ___EmotionJSX } from "@emotion/core";
var NotificationCenter = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
      children,
      i18n,
      id,
      inline = false,
      items,
      leftHeaderArea,
      leadingHeaderArea: leadingHeaderAreaProp,
      onClose,
      onScrollEnd,
      placement = 'bottom-start',
      rightHeaderArea,
      scrollEndOffset = 0,
      trailingHeaderArea: trailingHeaderAreaProp
    } = _ref,
    other = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var [open, setOpen] = React.useState(false);
  var [childrenNode, setChildrenNode] = React.useState(undefined);
  var rootRef = React.useRef();
  var handleRootRef = mergeRefs([rootRef, ref]);
  var style = useStyles({
    inline
  });
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      if (inline && children) {
        console.error(['@veneer/core: Notification Center has property inline=`true` and has a children declared.', 'This behavior is unsupported, please remove the children or set inline to `false`'].join('\n'));
      }
    }, [children, inline]);
  }
  var leadingHeaderArea = leadingHeaderAreaProp || leftHeaderArea;
  var trailingHeaderArea = trailingHeaderAreaProp || rightHeaderArea;
  var handleKeyDownEvent = React.useCallback(event => {
    if (inline) return;
    if (open && event.key === 'Escape') {
      setOpen(false);
      if (onClose) onClose(event);
    }
  }, [inline, onClose, open]);
  var handleClickOutside = React.useCallback(event => {
    if (inline) return;
    var isClickFromContextualMenu = event.target.getAttribute('data-contextual-menu') === 'true';
    if (isClickFromContextualMenu || !rootRef.current || !childrenNode || rootRef.current.contains(event.target) || childrenNode.contains(event.target)) {
      return;
    }
    if (open) {
      setOpen(false);
      if (onClose) onClose(event);
    }
  }, [childrenNode, inline, onClose, open]);
  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDownEvent);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDownEvent);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [handleClickOutside, handleKeyDownEvent, open]);
  var handleOnScrollEnd = React.useCallback(event => {
    if (onScrollEnd && scrollEnded(event.target, scrollEndOffset)) {
      onScrollEnd(event);
    }
  }, [onScrollEnd, scrollEndOffset]);
  var childProps = /*#__PURE__*/React.isValidElement(children) ? children.props : undefined;
  var handleChildrenClick = React.useCallback(event => {
    var {
      onClick
    } = childProps;
    var newOpen = !open;
    setOpen(newOpen);
    if (!newOpen && onClose) onClose(event.nativeEvent);
    if (onClick) onClick(event);
  }, [childProps, onClose, open]);
  var renderedChildren = React.useMemo(() => {
    if (inline || ! /*#__PURE__*/React.isValidElement(children)) {
      return null;
    }
    var childrenProps = {
      'aria-controls': open ? id : undefined,
      'aria-expanded': open,
      'aria-haspopup': true,
      forceActive: open,
      onClick: handleChildrenClick,
      ref: mergeRefs([childProps.ref, setChildrenNode])
    };
    var button = children.type === Button ? children : children.type === NotificationBadge ? /*#__PURE__*/React.isValidElement(children.props.children) && children.props.children.type === Button ? children.props.children : null : null;
    return children.type === Button ? /*#__PURE__*/React.cloneElement(children, childrenProps) : button && /*#__PURE__*/React.cloneElement(children, {
      children: /*#__PURE__*/React.cloneElement(button, childrenProps)
    });
  }, [childProps, children, handleChildrenClick, id, inline, open]);
  var content = ___EmotionJSX("div", _extends({}, other, {
    css: style.root,
    ref: handleRootRef
  }), ___EmotionJSX("div", {
    css: style.header
  }, ___EmotionJSX("div", null, leadingHeaderArea), ___EmotionJSX("span", {
    className: "subtitle-regular"
  }, (i18n === null || i18n === void 0 ? void 0 : i18n.title) || notificationCenterI18n.title), ___EmotionJSX("div", null, trailingHeaderArea)), !items || Array.isArray(items) && items.length === 0 ? ___EmotionJSX("div", {
    css: style.noNotifications
  }, (i18n === null || i18n === void 0 ? void 0 : i18n.noNotifications) || notificationCenterI18n.noNotifications) : ___EmotionJSX(Scrollbar, {
    component: "ul",
    customStyle: style.ul,
    onScroll: handleOnScrollEnd
  }, items.map(_ref2 => {
    var {
        id: itemId
      } = _ref2,
      itemProps = _objectWithoutProperties(_ref2, _excluded2);
    return ___EmotionJSX(NotificationCenterItem, _extends({
      actionButtonLabel: (i18n === null || i18n === void 0 ? void 0 : i18n.actionButton) || notificationCenterI18n.actionButton,
      id: itemId,
      key: itemId,
      open: inline ? true : open
    }, itemProps));
  })));
  var addPopover = notificationCenterContent => ___EmotionJSX(Popover, {
    anchorEl: childrenNode,
    id: id,
    keepMounted: true,
    open: open,
    placement: placement,
    role: "dialog",
    style: {
      zIndex: theme.elevation.popover
    },
    transition: true
  }, _ref3 => {
    var {
      placement: popoverPlacement,
      transitionProps
    } = _ref3;
    return ___EmotionJSX(Transition, _extends({}, transitionProps, {
      timeout: 150
    }), state => /*#__PURE__*/React.cloneElement(notificationCenterContent, {
      css: [style.root, style[state]],
      'data-placement': popoverPlacement.split('-')[0]
    }));
  });
  return ___EmotionJSX(React.Fragment, null, renderedChildren, inline ? content : addPopover(content));
});
NotificationCenter.propTypes = propTypes;
NotificationCenter.displayName = 'NotificationCenter';
export default NotificationCenter;